import { lazy, Suspense, useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ChatProvider } from '../../context/ChatContext';
import { InfoUpdatesProvider } from '../../context/InfoUpdatesContext';
import { NotificationProvider } from '../../context/NotificationContext';
import Header from '../header/Header';
import Sidebar from '../sidebar/Sidebar';
import RightPanel from '../rightpanel/RightPanel';
import BottomNav from '../bottomnav/BottomNav';
import { Helmet } from 'react-helmet-async';

const AuthModal = lazy(() => import('../auth/AuthModal'));

const MainLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [keyboardOpen, setKeyboardOpen] = useState(false);

    const searchParams = new URLSearchParams(location.search);
    const modalParam = searchParams.get('modal');
    const isAuthModalOpen = modalParam === 'login' || modalParam === 'signup';
    const authMode = modalParam === 'signup' ? 'signup' : 'login';

    useEffect(() => {
        const tags = document.head.querySelectorAll('[data-ssr]');
        tags.forEach(tag => tag.remove());
    }, []);

    useEffect(() => {
        if (!window.visualViewport || window.innerWidth >= 768) return;
        if (!location.pathname.startsWith('/messages')) {
            setKeyboardOpen(false);
            return;
        }

        const handleViewportResize = () => {
            const isKeyboard = window.visualViewport.height < window.innerHeight * 0.75;
            setKeyboardOpen(isKeyboard);
        };

        window.visualViewport.addEventListener('resize', handleViewportResize);
        return () => {
            window.visualViewport.removeEventListener('resize', handleViewportResize);
        };
    }, [location.pathname]);

    return (
        <ChatProvider>
        <NotificationProvider>
        <InfoUpdatesProvider>
        <div className={`App${keyboardOpen ? ' keyboard-open' : ''}`}>
            <Helmet titleTemplate="%s — Pixel" />
            <Header onLoginClick={() => {
                const newSearch = new URLSearchParams(location.search);
                newSearch.set('modal', 'login');
                navigate(`${location.pathname}?${newSearch.toString()}`, { replace: true });
            }} />
            <div className="app__inner">
                <div className="app__layout">
                    <Sidebar />
                    <main className="app__main">
                        <Outlet />
                    </main>
                    <RightPanel />
                </div>
            </div>
            <BottomNav />
            {isAuthModalOpen && !user && (
                <Suspense fallback={null}>
                    <AuthModal isOpen={true} initialMode={authMode} />
                </Suspense>
            )}
        </div>
        </InfoUpdatesProvider>
        </NotificationProvider>
        </ChatProvider>
    );
};

export default MainLayout;
